import { UploadFile } from '@/types';

export type ImageSize = 'large' | 'medium' | 'small' | 'thumbnail' | 'logo' | 'url';

/** Base URL for Strapi uploads (CloudFront in prod, Strapi origin in dev). */
function getUploadsBaseUrl(): string {
  return (
    (typeof process !== 'undefined' &&
      (process.env.NEXT_PUBLIC_EXTERNAL_IMAGES_DOMAIN || process.env.EXTERNAL_IMAGES_DOMAIN)) ||
    'http://localhost:1337'
  );
}

/** Whether we're using an external media domain (S3/CloudFront) rather than Strapi directly. */
function hasExternalImagesDomain(): boolean {
  return !!(
    typeof process !== 'undefined' &&
    (process.env.NEXT_PUBLIC_EXTERNAL_IMAGES_DOMAIN || process.env.EXTERNAL_IMAGES_DOMAIN)
  );
}

/**
 * Media is served from S3 at bucket root (no /uploads/ prefix). Old Strapi entries
 * may have /uploads/ in the stored URL; strip it so the request hits the correct path.
 */
function stripUploadsPrefix(url: URL): URL {
  const path = url.pathname;
  if (path.startsWith('/uploads/') || path === '/uploads') {
    const without = path === '/uploads' ? '' : path.slice('/uploads'.length);
    url.pathname = without || '/';
    return url;
  }
  return url;
}

/**
 * Returns a usable URL for a Strapi UploadFile.
 * Relative /uploads/ paths are rewritten to the public media domain (CloudFront).
 * A leading /uploads/ segment is stripped so requests hit the bucket root where objects are stored.
 */
export function getImageUrl({
  image,
  size = 'medium',
}: {
  image?: UploadFile | null;
  size?: ImageSize;
}): string | null {
  if (!image) return null;
  const raw = size === 'url' ? image.url : (image.formats?.[size]?.url ?? image.url);
  if (!raw) return null;

  const baseUrl = getUploadsBaseUrl();

  try {
    const parsed = new URL(raw);
    // Already absolute: normalize /uploads/ so media domain serves from bucket root
    if (hasExternalImagesDomain() && parsed.origin === new URL(baseUrl).origin) {
      return stripUploadsPrefix(parsed).toString();
    }
    return raw;
  } catch {
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    const url = new URL(path, baseUrl);
    // Only strip /uploads/ prefix when using external S3/CloudFront domain
    if (hasExternalImagesDomain()) {
      return stripUploadsPrefix(url).toString();
    }
    return url.toString();
  }
}
